import React, { useState, useEffect, useContext, useMemo } from "react";
import qs from "qs";
import { useLocation } from "react-router-dom";
import {
  GetBlankWorkItem,
  GetTaskTypes,
  GetDefaultTaskType,
  SetBusinessDays,
  initialValues,
  isUseCase3,
  validationSchema,
} from "../helpers/task-helpers";
import { LoginContext } from "../../../../../services/api/authentication-api";
import TaskDetailsSection from "../common-components/task-details-section";
import { Form } from "react-bootstrap";
import { Formik } from "formik";
import DynamicTaskDetails from "../common-components/dynamic-task-details";
import TaskAssignMemberWrapper from "../../../../../components/TaskAssignMemberWrapper/TaskAssignMemberWrapper";
import TaskAssignMemberMultiOwnerWrapper from "../../../../../components/TaskAssignMemberWrapper/TaskAssignMemberMultiOwnerWrapper";
import "./task-create.scss";
import TaskCreateEditComments from "../common-components/task-create-edit-comments";
import { ToBase64 } from "../../../../../util/file-helpers";
import { AddStandardTaskWeb, IsBaseWorkflowSuppressed } from "../../../../../services/api/task-api";
import { TaskStatusTypes } from "../../tasks-actions-panel/task-status-types";
import { isDynamicFieldsValid } from "../../../../../components/DynamicField/Tasks/utils/utils";
import TaskCreateTop from "./task-create-top";
import TaskCreateBottom from "./task-create-bottom";
import { PulseLoader } from "react-spinners";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { errorNotification } from "../../../../../redux/notifications";
import { connect } from 'react-redux';
import { GetUserProjectsForTaskCreateEdit } from "../../../../../services/api/project-api";
import { AddMembersAndGroupsToProject } from "../../../../../services/api/user-api";

const TaskCreate = ({ errorNotification, dateFormatID, onCreateAnother }) => {
  const [standardTask, setStandardTask] = useState({});
  const [projectID] = useState(0);
  const [activeProjects, setActiveProjects] = useState([]);
  const [workItemTypes, setWorkItemTypes] = useState([]);
  const [workItemType, setWorkItemType] = useState();
  const [showCreatedModal, setShowCreatedModal] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newCommentText, setNewCommentText] = useState("");
  const [dynamicFields, setDynamicFields] = useState([]); // state to keep track of dynamic fields; see tasks-dynamic-fields.js for more details
  const [dynamicCommentField, setCommentDynamicFields] = useState([]); // state to keep track of dynamic fields; see tasks-dynamic-fields.js for more details
  const [showUseCase3, setShowUseCase3] = useState(false);
  const [saveValues, setSaveValues] = useState([]);
  const [saveActions, setSaveActions] = useState([]);
  const [disableDynamicDetailErrors, setDisableDynamicDetailErrors] = useState(true);
  const [taskMembers, setTaskMembers] = useState([]);
  const [taskOtherMembers, setTaskOtherMembers] = useState([]);
  const [selectedMembersEdit, setSelectedMembersEdit] = useState([]);
  const [selectedMembersView, setSelectedMembersView] = useState([]);
  const [selectedMembersApprove, setSelectedMembersApprove] = useState([]);
  const [selectedMembersOwner, setSelectedMembersOwner] = useState([]);
  const [selectedGroupEdit, setSelectedGroupEdit] = useState([]);
  const [selectedGroupView, setSelectedGroupView] = useState([]);
  const [selectedGroupApprove, setSelectedGroupApprove] = useState([]);
  const [defaultTaskType, setDefaultTaskType] = useState(0);
  const [alertConfig, setAlertConfig] = useState({
    showAlert: false,
    alertAction: "",
    alertMessage: "",
    alertVariant: "danger",
  });
  const [projectIsMultiOwner, setProjectIsMultiOwner] = useState(false);
  const user = useContext(LoginContext);
  const labels = user?.currentUser?.objectLabels;
  useEffect(() => {
    setSelectedMembersOwner([user.userID]);
  }, [user.userID]);
  const location = useLocation();
  const IsMultiOwner = projectIsMultiOwner;

  // Memoize Formik initialValues. Without useMemo, initialValues() returns a
  // fresh object (with `new Date()` for Start/Due) on every render. Combined
  // with enableReinitialize=true, Formik would reset the form on every parent
  // re-render — wiping user input mid-editing. Recompute only when projectID,
  // the async-fetched defaultTaskType, or the workItemTypes list actually
  // changes. workItemTypes is included so Due is computed from the default
  // task type's duration once both async fetches resolve.
  const formInitialValues = useMemo(
    () => initialValues(projectID, defaultTaskType, workItemTypes),
    [projectID, defaultTaskType, workItemTypes]
  );

  useEffect(() => {
    SetBusinessDays();

    const fetchDefaultTaskType = async () => {
      try {
        const defaultTaskTypeId = await GetDefaultTaskType();
        setDefaultTaskType(defaultTaskTypeId);
        setWorkItemType(defaultTaskTypeId);
      } catch (error) {
        console.error("Failed to fetch default task type", error);
        setDefaultTaskType(0);
      }
    };

    let result = GetBlankWorkItem(user.userID);
    let url = qs.parse(location.search, { ignoreQueryPrefix: true });
    result.ProjectId = url.project ?? 0;

    GetProjects(result);
    fetchDefaultTaskType();
    GetTaskTypes(setWorkItemTypes);
  }, []);

  const GetProjects = async (result) => {
      const response = await GetUserProjectsForTaskCreateEdit();
      if(response.status === 200) {
        const projects = response.data;
        setActiveProjects(projects);
        setStandardTask(result);
        setLoaded(true);
      }
      else{
        setAlertConfig((prev) => ({
          ...prev,
          showAlert: true,
          alertAction: "Cannot get projects",
          alertMessage: response?.message?.toString(),
          alertVariant: "danger",
        }));
      }
  };

  const handleProjectSelected = (projectIsMultiOwnerValue) => {
    setProjectIsMultiOwner(projectIsMultiOwnerValue ?? false);
  };

  const onSubmit = async (values, actions) => {
    setAlertConfig(() => ({
      showAlert: false,
      alertAction: "",
      alertMessage: "",
      alertVariant: "danger",
    }));
    if(!IsMultiOwner){
      setSelectedMembersOwner([user.userID]);
    }
    // TAD-5798: when Sync Member Permissions drives membership for the selected task type, suppress the
    // legacy "lone owner becomes editor" force-add (the backend suppresses the same normalization).
    // Resolves to false for non-Sync task types / on error, so existing behavior is unchanged.
    const suppressBaseWorkflow = await IsBaseWorkflowSuppressed(values.workItemType);
    if (
      !isUseCase3(
        selectedMembersApprove,
        selectedMembersEdit,
        setSelectedMembersEdit,
        selectedMembersView,
        selectedMembersOwner,
        suppressBaseWorkflow,
      )
    ) {
      createStandardTask(values, actions, false);
    } else {
      setSaveActions(actions);
      setSaveValues(values);
      setShowUseCase3(true);
    }
  };

  const ownerHasPermission = () => {
    return selectedMembersOwner.every(
      (ownerId) =>
        selectedMembersView.includes(ownerId) ||
        selectedMembersEdit.includes(ownerId) ||
        selectedMembersApprove.includes(ownerId)
    );
  };

  const memberPermissionValidation = () => {
    if (selectedMembersEdit.length === 0 && ownerHasPermission()) {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: "Please assign at least one member as an editor",
        alertMessage: "",
        alertVariant: "danger",
      }));
      return false;
    }
    return true;
  };

  const createStandardTask = async (values, actions, isUseCase3Ready) => {
    let startDate = new Date(values.Start);
    let endDate = new Date(values.Due);
    startDate = startDate.toISOString();
    endDate = endDate.toISOString();
    startDate = startDate.split("T")[0];
    endDate = endDate.split("T")[0];
    let projStartDate = values.selectedProject?.Start;
    let projEndDate = values.selectedProject?.End;
    projStartDate = projStartDate?.split("T")[0];
    projEndDate = projEndDate?.split("T")[0];

    if (startDate > endDate) {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: "Start date is greater than due date",
        alertMessage: "",
        alertVariant: "danger",
      }));
      actions.setSubmitting(false);
      return {};
    }

    if (
      startDate < projStartDate ||
      startDate > projEndDate ||
      endDate > projEndDate ||
      endDate < projStartDate
    ) {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction:
          "Invalid start/due dates, please compare to project start/due dates before submitting",
        alertMessage: "",
        alertVariant: "danger",
      }));
      actions.setSubmitting(false);
      return {};
    }

    if (!memberPermissionValidation()) {
      actions.setSubmitting(false);
      return;
    }

    const convertedFiles = await Promise.all(
      selectedFiles.map(async (file) => {
        const result = await ToBase64(file);
        let mimetype = result.substr(
          result.indexOf(":") + 1,
          result.indexOf(";") - result.indexOf(":") - 1
        );
        let thedocAsBytes = result.split(",")[1];
        return {
          FileName: file.name,
          FileContentType: mimetype,
          FileByteArray: thedocAsBytes,
        };
      })
    );

    let obj = {
      ProjectId: values.ProjectId,
      Name: values.Name,
      WorkItemTypeId: values.workItemType,
      Priority: parseInt(values.PriorityId),
      Start: values.Start,
      Due: values.Due,
      SelectedMembersView: selectedMembersView,
      SelectedMembersEdit: selectedMembersEdit,
      SelectedMembersApprove: selectedMembersApprove,
      selectedMembersOwner: selectedMembersOwner.length > 0 ? selectedMembersOwner : [user.userID],
      SelectedGroupEdit: selectedGroupEdit,
      SelectedGroupView: selectedGroupView,
      SelectedGroupApprove: selectedGroupApprove,
      dynamicFields: dynamicFields?.map((field) => ({
        DynamicFieldId: field.Id,
        Data: field.Data,
        Table: field.Table,
        ObjectId: 0,
        Files: field.Files,
      })),
      dynamicFieldsComments: dynamicCommentField.map((field) => ({
        DynamicFieldId: field.Id,
        Data: field.Data,
        ObjectId: 0,
      })),
      taskCommentFiles: convertedFiles,
      CommentText: newCommentText,
      StatusId: isUseCase3Ready
        ? TaskStatusTypes.PENDINGAPPROVAL
        : TaskStatusTypes.ASSIGNED,
      CreatedById: user.userID,
      IsMultiOwner: IsMultiOwner,
    };
    setLoaded(false);

    let response = await AddStandardTaskWeb(obj);

    if (response.status === 200) {
      if (response.data.Message === "Duplicate task detected") {
          errorNotification({
              header: `A TRIYO Automation was executed for ${values.Name}`,
              message: `${values.Name} Task was automatically moved to a ‘Cancelled’ State and Resolved to Complete.`,
          });
      }
      let assignedUsers = [...selectedMembersEdit, ...selectedMembersView, ...selectedMembersApprove];
      let assignedGroups = [...selectedGroupEdit, ...selectedGroupView, ...selectedGroupApprove];
      let usersToBeAdded = assignedUsers.filter((user) => !values.selectedProject.Members.some((member) => member.UserId === user));
      let groupsToBeAdded = assignedGroups.filter((group) => !values.selectedProject.Groups.some((member) => member.UserId === group));
      if (usersToBeAdded.length > 0 || groupsToBeAdded.length > 0) {
        let obj ={
          UserIds : usersToBeAdded,
          GroupIds : groupsToBeAdded,
          ProjectId : values.selectedProject.Id
        }
        AddMembersAndGroupsToProject(obj)
      }
      setShowCreatedModal(true);
      setLoaded(true);
    } else {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: "Error creating task",
        alertMessage: response?.response?.data,
        alertVariant: "danger",
      }));
      setLoaded(true);
    }
  };

  const onValidation = (dynamicFields, setDynamicFields) => {
    const errors = {};
    if (!isDynamicFieldsValid(dynamicFields)) {
      setDynamicFields(
        dynamicFields.map((field) => ({ ...field, touched: true }))
      );
      errors.dynamicFields = "Dynamic Fields are Invalid";
    }
    return errors;
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const SetIsUseCase3Ready = (isUseCase3Ready) => {
    createStandardTask(saveValues, saveActions, isUseCase3Ready);
  };

  return (
    <div className="task-create">
      <Helmet>
        <title>
          TRIYO · Create {labels?.TaskSingular ?? 'Task'}
        </title>
      </Helmet>
      {!loaded ?
        <div className="loading-overlay " >
          <div className="loading-spinner-wrapper">
            <PulseLoader className="loading-spinner"></PulseLoader>
          </div>
        </div>
        : (
          <div className="task-create-standard-panel">
            <TaskCreateTop
              alertConfig={alertConfig}
              setAlertConfig={setAlertConfig}
              showCreatedModal={showCreatedModal}
              showUseCase3={showUseCase3}
              SetIsUseCase3Ready={SetIsUseCase3Ready}
              setShowUseCase3={setShowUseCase3}
              labels={labels}
              onCreateAnother={onCreateAnother}
            ></TaskCreateTop>

            <div className="task-edit-standard-panel">
              <div className="edit-container">
                <Formik
                  validationSchema={validationSchema(labels)}
                  initialValues={formInitialValues}
                  enableReinitialize={true}
                  onSubmit={(values, actions) => onSubmit(values, actions)}
                  validate={() => onValidation(dynamicFields, setDynamicFields)}
                >
                  {({
                    handleSubmit,
                    handleChange,
                    values,
                    touched,
                    errors,
                    setFieldValue,
                    isSubmitting,
                    submitForm,
                    actions,
                  }) => (
                    <Form
                      onSubmit={handleSubmit}
                      noValidate
                      className="create-container"
                      onKeyDown={onKeyDown}
                    >
                      <div className="form-container">
                        <div className="task-top-section">
                          <h4 className="heading">{labels?.TaskSingular ?? 'Task'} Detail</h4>
                          <TaskDetailsSection
                            values={values}
                            touched={touched}
                            errors={errors}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            workItemTypes={workItemTypes}
                            activeProjects={activeProjects}
                            dateFormatID={dateFormatID}
                            setTaskOtherMembers={setTaskOtherMembers}
                            borderBottom={dynamicFields?.length === 0}
                            setWorkItemType={setWorkItemType}
                            labels={labels}
                            onProjectSelected={handleProjectSelected}
                          />
                          <DynamicTaskDetails
                            standardTask={standardTask}
                            values={values}
                            touched={touched}
                            dynamicFields={dynamicFields}
                            setDynamicFields={setDynamicFields}
                          />
                          <div className="task-row-wrapper">
                            <div className="taskForm">
                              <TaskCreateEditComments
                                projectTypeId={standardTask.ProjectTypeId}
                                taskTypeId={values.workItemType}
                                dynamicFields={dynamicCommentField}
                                setDynamicFields={setCommentDynamicFields}
                                touched={touched}
                                taskMembers={taskMembers}
                                newCommentText={newCommentText}
                                setNewCommentText={setNewCommentText}
                                allUsers={taskOtherMembers.map(
                                  (member) => member.User.Name
                                )}
                                selectedFiles={selectedFiles}
                                setSelectedFiles={setSelectedFiles}
                                setAlertConfig={setAlertConfig}
                                alertConfig={alertConfig}
                              />
                            </div>
                            {IsMultiOwner ? (
                              <TaskAssignMemberMultiOwnerWrapper
                                isCreate={true}
                                task={standardTask}
                                taskMembers={taskMembers}
                                setTaskMembers={setTaskMembers}
                                projectMembers={taskOtherMembers}
                                selectedMembersEdit={selectedMembersEdit}
                                setSelectedMembersEdit={setSelectedMembersEdit}
                                selectedMembersView={selectedMembersView}
                                setSelectedMembersView={setSelectedMembersView}
                                selectedMembersApprove={selectedMembersApprove}
                                setSelectedMembersApprove={setSelectedMembersApprove}
                                selectedGroupEdit={selectedGroupEdit}
                                setSelectedGroupEdit={setSelectedGroupEdit}
                                selectedGroupView={selectedGroupView}
                                setSelectedGroupView={setSelectedGroupView}
                                selectedGroupApprove={selectedGroupApprove}
                                setSelectedGroupApprove={setSelectedGroupApprove}
                                workItemType={workItemType}
                                selectedMembersOwner={selectedMembersOwner}
                                setSelectedMembersOwner={setSelectedMembersOwner}
                              />
                            ) : (
                              <TaskAssignMemberWrapper
                                isCreate={true}
                                task={standardTask}
                                taskMembers={taskMembers}
                                setTaskMembers={setTaskMembers}
                                projectMembers={taskOtherMembers}
                                selectedMembersEdit={selectedMembersEdit}
                                setSelectedMembersEdit={setSelectedMembersEdit}
                                selectedMembersView={selectedMembersView}
                                setSelectedMembersView={setSelectedMembersView}
                                selectedMembersApprove={selectedMembersApprove}
                                setSelectedMembersApprove={setSelectedMembersApprove}
                                selectedGroupEdit={selectedGroupEdit}
                                setSelectedGroupEdit={setSelectedGroupEdit}
                                selectedGroupView={selectedGroupView}
                                setSelectedGroupView={setSelectedGroupView}
                                selectedGroupApprove={selectedGroupApprove}
                                setSelectedGroupApprove={setSelectedGroupApprove}
                                workItemType={workItemType}
                              />
                            )}
                          </div>
                          <TaskCreateBottom
                            isSubmitting={isSubmitting}
                            setDisableDynamicDetailErrors={
                              setDisableDynamicDetailErrors
                            }
                            labels={labels}
                          />
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

TaskCreate.propTypes = {
  dateFormatID: PropTypes.string,
  errorNotification: PropTypes.func,
  onCreateAnother: PropTypes.func.isRequired,
};

export default connect(
  null,
  (dispatch) => ({
      errorNotification: (notification) => dispatch(errorNotification(notification)),
  })
)(TaskCreate);